/**
 * Route utilities for NaraHub.
 * Provides centralized URL building for workspace-scoped routes.
 */

import { useWorkspace } from "../contexts/WorkspaceContext";

/**
 * Build URLs for a workspace handle.
 * All workspace-scoped routes use the pattern: /ws/:handle/...
 */
export function wsUrl(handle: string) {
  const base = `/ws/${handle}`;

  return {
    // Dashboard
    dashboard: `${base}/dashboard`,

    // Projects
    projects: `${base}/projects`,
    project: (id: string) => `${base}/project/${id}`,
    projectCollaborators: (id: string) => `${base}/project/${id}/collaborators`,
    projectGenerate: (id: string) => `${base}/project/${id}/generate`,
    projectCatalog: (id: string) => `${base}/project/${id}/catalog`,

    // Documents
    document: (projectId: string, docId: string) =>
      `${base}/project/${projectId}/document/${docId}`,
    documentTranscriptEdit: (projectId: string, docId: string) =>
      `${base}/project/${projectId}/document/${docId}/transcript/edit`,
    documentMarkdownEdit: (projectId: string, docId: string) =>
      `${base}/project/${projectId}/document/${docId}/edit`,
    documentGenerate: (projectId: string, docId: string) =>
      `${base}/project/${projectId}/document/${docId}/generate`,

    // Collaborators
    collaborators: `${base}/collaborators`,
    collaborator: (id: string) => `${base}/collaborators/${id}`,

    // Business profiles (owner/admin)
    businessProfiles: `${base}/business-profiles`,

    // Trash
    trash: `${base}/trash`,
  };
}

/**
 * Hook for building workspace-scoped URLs in components.
 * Returns null if no active workspace with a handle is available.
 */
export function useWorkspaceUrl() {
  const { activeWorkspace } = useWorkspace();
  return activeWorkspace?.handle ? wsUrl(activeWorkspace.handle) : null;
}

/**
 * Hook that returns workspace URL builder or throws if not available.
 * Use this when you're certain you're within a workspace context.
 */
export function useRequiredWorkspaceUrl() {
  const urls = useWorkspaceUrl();
  if (!urls) {
    throw new Error("useRequiredWorkspaceUrl must be used within a workspace context with a valid handle");
  }
  return urls;
}

/**
 * NaraChef is disabled while the backend (/chef/*) is offline.
 * Flip back to true once nara-comms mounts /chef/* again.
 */
export const CHEF_ENABLED = false;

/**
 * Static admin routes (not workspace-scoped).
 * These are at /admin/...
 */
export const adminRoutes = {
  users: "/admin/users",
  user: (id: string) => `/admin/users/${id}`,
  workspaces: "/admin/workspaces",
  workspace: (id: string) => `/admin/workspaces/${id}`,
  workspaceCollaborator: (workspaceId: string, userId: string) =>
    `/admin/workspaces/${workspaceId}/collaborators/${userId}`,
  workspaceProject: (workspaceId: string, projectId: string) =>
    `/admin/workspaces/${workspaceId}/projects/${projectId}`,
  // Document recipes (NaraShape)
  documentRecipes: "/admin/document-recipes",
  documentRecipe: (id: string) => `/admin/document-recipes/${id}`,
  // Project templates
  projectTemplates: "/admin/project-templates",
  projectTemplate: (id: string) => `/admin/project-templates/${id}`,
  // Recipe Chef
  chef: "/admin/chef",
  // Wizards
  wizards: "/admin/wizards",
  wizardHelp: "/admin/wizards/help",
  wizard: (id: string) => `/admin/wizards/${id}`,
  wizardEntry: (id: string) => `/admin/wizard-entries/${id}`,
  settings: "/admin/settings",
  infrastructure: "/admin/infrastructure",
  architecture: "/admin/architecture",
  boostUsage: "/admin/boost-usage",
  activity: "/admin/activity",
  boostPricing: "/admin/boost-pricing",
  stripeProducts: "/admin/stripe-products",
  trials: "/admin/trials",
  workflows: "/admin/workflows",
};

/**
 * Static public/auth routes (not workspace-scoped).
 */
export const publicRoutes = {
  login: "/login",
  home: "/",
  dashboard: "/dashboard",
  profile: "/profile",
  workspaceSettings: "/workspace/settings",
  workspaceTags: "/workspace/tags",
  pricing: "/pricing",
  billing: "/billing",
  billingHistory: "/billing-history",
  boostsHistory: "/boosts-history",
};
